import React from "react";
import { useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { trpcClient } from "@/lib/trpc";
import ReceiptForm from "./ReceiptForm";
import { Skeleton } from "@/components/ui/skeleton";
import PrintDialog from "@/components/shared/PrintDialog";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";

import { toVnDateString } from "@apv-erp/status";
export default function ReceiptEditPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { receiptId } = useParams({ strict: false }) as any;
  const [isPrinting, setIsPrinting] = React.useState(false);

  const { data: receipt, isLoading } = useQuery({
    queryKey: ["receipt", receiptId],
    queryFn: () => trpcClient.receipts.getById.query({ id: receiptId }),
    enabled: !!receiptId,
  });

  const handleSubmit = async (data: any) => {
    try {
      await trpcClient.receipts.update.mutate({ id: receiptId, data });

      await queryClient.invalidateQueries({ queryKey: ["receipts"] });
      await queryClient.invalidateQueries({ queryKey: ["receipt", receiptId] });
      await queryClient.invalidateQueries({ queryKey: ["customer-debts"] });
      navigate({ to: `/finance/receipts/${receiptId}` });
    } catch (error: any) {
      toast.error(error?.message || "Có lỗi xảy ra khi lưu");
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 w-full mx-auto space-y-6">
        <Skeleton className="h-10 w-[200px]" />
        <div className="bg-white p-6 rounded-xl border border-slate-200">
          <Skeleton className="h-[400px] w-full" />
        </div>
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="flex flex-col items-center justify-center h-[50vh] text-slate-500">
        <p>Không tìm thấy thông tin phiếu thu</p>
        <Button
          variant="link"
          onClick={() => navigate({ to: "/finance/receipts" })}
        >
          Quay lại danh sách
        </Button>
      </div>
    );
  }

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "Phiếu Thu", to: "/finance/receipts" },
        { label: receipt.receiptCode || receiptId, to: `/finance/receipts/${receiptId}` },
        { label: "Chỉnh sửa" },
      ]}
      title={`Sửa ${receipt.receiptCode || receiptId}`}
      onBack={() => navigate({ to: `/finance/receipts/${receiptId}` })}
      showContextPanel={false}
      actions={
        <Button
          variant="outline"
          size="sm"
          className="h-8 gap-1.5"
          onClick={() => setIsPrinting(true)}
        >
          <Printer className="w-3.5 h-3.5 text-slate-600" /> <span>In</span>
        </Button>
      }
    >
      <div className="p-4 md:p-6 w-full mx-auto">
        <div className="bg-white p-6 rounded-xl border border-slate-200 shadow-xs">
          <ReceiptForm
            defaultValues={
              {
                ...receipt,
                // API trả Decimal dạng chuỗi; form yêu cầu số (trước đây sửa phiếu báo "amount phải là số")
                amount: Number((receipt as any).amount ?? 0),
                receiptDate: receipt.receiptDate
                  ? toVnDateString(receipt.receiptDate)
                  : "",
              } as any
            }
            onSubmit={handleSubmit}
            onCancel={() => navigate({ to: `/finance/receipts/${receiptId}` })}
            submitLabel={(receipt as any)?.status === "DRAFT" ? "Lưu" : "Cập nhật"}
          />
        </div>
      </div>

      <PrintDialog
        open={isPrinting}
        onClose={() => setIsPrinting(false)}
        type="receipt"
        data={receipt}
      />
    </StandardDetailLayout>
  );
}
